import React, { useState } from 'react';
import { View, StyleSheet, Modal, Pressable, ScrollView, Platform, Linking, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { calculateDetailedRoute, calculateRouteWithOfflineFallback, type DetailedRoute, type TravelMode } from '@/services/navigation';
import type { LocationCoordinates } from '@/services/location';
import type { MapPin } from './MapView';
import { TurnByTurnNavigation } from '@/components/navigation/TurnByTurnNavigation';

type DirectionsPanelProps = {
  visible: boolean;
  start: LocationCoordinates | null;
  destination: LocationCoordinates;
  /** Prefer text for "Open in Maps" when coords may be wrong (same as itinerary View on map). */
  destinationAddressQuery?: string | null;
  destinationName?: string;
  onClose: () => void;
  onOpenExternalMaps?: () => void;
  preCalculatedRoute?: DetailedRoute | null;
  showTurnByTurn?: boolean;
  currentLocation?: LocationCoordinates | null;
};

export const DirectionsPanel: React.FC<DirectionsPanelProps> = ({
  visible,
  start,
  destination,
  destinationAddressQuery,
  destinationName,
  onClose,
  onOpenExternalMaps,
  preCalculatedRoute,
  showTurnByTurn = false,
  currentLocation,
}) => {
  const theme = useTheme();
  const [route, setRoute] = useState<DetailedRoute | null>(null);
  const [loading, setLoading] = useState(false);
  const [travelMode, setTravelMode] = useState<TravelMode>('driving');
  const [error, setError] = useState<string | null>(null);
  const [navigationMode, setNavigationMode] = useState(false);

  // Use pre-calculated route if available (only for initial load and same travel mode)
  React.useEffect(() => {
    // Only use pre-calculated route if it's for driving mode (default)
    // If user changes mode, we need to recalculate
    if (preCalculatedRoute && travelMode === 'driving') {
      setRoute(preCalculatedRoute);
      setError(null);
      setLoading(false);
    }
  }, [preCalculatedRoute, travelMode]);

  // Calculate route when panel opens or travel mode changes
  React.useEffect(() => {
    if (visible && start && destination.latitude !== 0 && destination.longitude !== 0) {
      // If we have a pre-calculated route for driving mode and user is on driving mode, skip
      if (preCalculatedRoute && travelMode === 'driving') {
        return;
      }
      
      // Small delay to ensure panel is rendered
      const timer = setTimeout(() => {
        loadRoute();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setRoute(null);
      setError(null);
      setLoading(false);
    }
  }, [visible, start, destination, travelMode, preCalculatedRoute]);

  const loadRoute = async () => {
    if (!start || (start.latitude === 0 && start.longitude === 0)) {
      setError('Starting location not available. Please enable location services.');
      setLoading(false);
      return;
    }
    
    if (destination.latitude === 0 && destination.longitude === 0) {
      setError('Destination not available');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (__DEV__) {
        console.log('[DirectionsPanel] Calculating route:', {
          start,
          destination,
          mode: travelMode,
        });
      }
      
      // Use offline fallback if available
      const routeId = `route_${start.latitude}_${start.longitude}_${destination.latitude}_${destination.longitude}_${travelMode}`;
      const detailedRoute = await calculateRouteWithOfflineFallback(
        start,
        destination,
        travelMode,
        undefined,
        routeId
      );
      if (detailedRoute && detailedRoute.geometry && detailedRoute.geometry.length > 0) {
        if (__DEV__) {
          console.log('[DirectionsPanel] Route calculated successfully:', {
            steps: detailedRoute.steps.length,
            geometry: detailedRoute.geometry.length,
            distance: detailedRoute.distance,
            duration: detailedRoute.duration,
          });
        }
        setRoute(detailedRoute);
      } else {
        const errorMsg = 'Could not calculate route. Please try again or use external maps.';
        if (__DEV__) {
          console.warn('[DirectionsPanel] Route calculation returned empty result');
        }
        setError(errorMsg);
      }
    } catch (err) {
      console.error('[DirectionsPanel] Error loading route:', err);
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Failed to calculate route. Please check your connection and try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenExternalMaps = () => {
    const appleMode = travelMode === 'walking' ? 'w' : travelMode === 'transit' ? 'r' : 'd';
    const googleMode = travelMode === 'walking' ? 'w' : travelMode === 'transit' ? 'r' : 'd';
    const webMode = travelMode === 'walking' ? 'walking' : travelMode === 'transit' ? 'transit' : 'driving';

    if (onOpenExternalMaps) {
      onOpenExternalMaps();
    } else {
      const q = destinationAddressQuery?.trim();
      const enc = q ? encodeURIComponent(q) : null;
      const url = enc
        ? Platform.select({
            ios: `maps://maps.apple.com/?daddr=${enc}&dirflg=${appleMode}`,
            android: `google.navigation:q=${enc}&mode=${googleMode}`,
            default: `https://www.google.com/maps/dir/?api=1&destination=${enc}&travelmode=${webMode}`,
          })
        : Platform.select({
            ios: `maps://maps.apple.com/?daddr=${destination.latitude},${destination.longitude}&dirflg=${appleMode}`,
            android: `google.navigation:q=${destination.latitude},${destination.longitude}&mode=${googleMode}`,
            default: `https://www.google.com/maps/dir/?api=1&destination=${destination.latitude},${destination.longitude}&travelmode=${webMode}`,
          });
      if (url) {
        Linking.openURL(url).catch((err) => {
          console.error('[DirectionsPanel] Error opening maps:', err);
        });
      }
    }
    onClose();
  };

  const formatDistance = (meters: number): string => {
    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    }
    return `${(meters / 1000).toFixed(1)} km`;
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getModeIcon = (mode: TravelMode) => {
    switch (mode) {
      case 'walking':
        return 'walk';
      case 'driving':
        return 'car';
      case 'transit':
        return 'bus';
      default:
        return 'navigate';
    }
  };

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <Pressable style={[styles.overlay, { backgroundColor: theme.colors.modalOverlay }]} onPress={onClose}>
        <Pressable
          style={[
            styles.panel,
            {
              backgroundColor: theme.colors.surface,
            },
          ]}
          onPress={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Ionicons name="navigate" size={24} color={theme.colors.primary} />
              <View style={styles.headerText}>
                <ThemedText variant="h3">Directions</ThemedText>
                {destinationName && (
                  <Caption color={theme.colors.textSecondary}>{destinationName}</Caption>
                )}
              </View>
            </View>
            <Pressable onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
            </Pressable>
          </View>

          {/* Travel Mode Selector */}
          <View style={styles.modeSelector}>
            {(['walking', 'driving', 'transit'] as TravelMode[]).map((mode) => (
              <Pressable
                key={mode}
                onPress={() => setTravelMode(mode)}
                style={[
                  styles.modeButton,
                  {
                    backgroundColor:
                      travelMode === mode
                        ? theme.colors.primary + '15'
                        : theme.colors.background,
                    borderColor:
                      travelMode === mode ? theme.colors.primary : theme.colors.borderMuted,
                  },
                ]}
              >
                <Ionicons
                  name={getModeIcon(mode) as any}
                  size={20}
                  color={travelMode === mode ? theme.colors.primary : theme.colors.textSecondary}
                />
                <Caption
                  color={travelMode === mode ? theme.colors.primary : theme.colors.textSecondary}
                  style={{ marginTop: 4, textTransform: 'capitalize' }}
                >
                  {mode}
                </Caption>
              </Pressable>
            ))}
          </View>

          {/* Content */}
          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            {loading && (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={theme.colors.primary} />
                <Body color={theme.colors.textSecondary} style={{ marginTop: 16 }}>
                  Calculating route...
                </Body>
              </View>
            )}

            {error && !loading && (
              <View style={styles.errorContainer}>
                <Ionicons name="alert-circle-outline" size={48} color={theme.colors.error || '#FF3B30'} />
                <Body color={theme.colors.error || '#FF3B30'} style={{ marginTop: 16, textAlign: 'center' }}>
                  {error}
                </Body>
                <Button
                  label="Try Again"
                  onPress={loadRoute}
                  variant="primary"
                  style={{ marginTop: 16 }}
                />
              </View>
            )}

            {route && !loading && !error && (
              <>
                {/* Route Summary */}
                <Card variant="outlined" style={styles.summaryCard}>
                  <View style={styles.summaryRow}>
                    <View style={styles.summaryItem}>
                      <Ionicons name="time-outline" size={20} color={theme.colors.primary} />
                      <View style={styles.summaryText}>
                        <Caption color={theme.colors.textSecondary}>Duration</Caption>
                        <Body style={{ fontWeight: '600' }}>{formatDuration(route.duration)}</Body>
                      </View>
                    </View>
                    <View style={styles.summaryItem}>
                      <Ionicons name="resize-outline" size={20} color={theme.colors.primary} />
                      <View style={styles.summaryText}>
                        <Caption color={theme.colors.textSecondary}>Distance</Caption>
                        <Body style={{ fontWeight: '600' }}>{formatDistance(route.distance)}</Body>
                      </View>
                    </View>
                  </View>
                </Card>

                {/* Turn-by-turn Instructions */}
                {route.steps && route.steps.length > 0 ? (
                  <View style={styles.stepsContainer}>
                    <SubHeading style={styles.sectionTitle}>Turn-by-turn Directions</SubHeading>
                    {route.steps.map((step, index) => (
                      <View key={index} style={styles.stepItem}>
                        <View style={styles.stepNumber}>
                          <ThemedText variant="bodyStrong" style={{ color: theme.colors.surface }}>
                            {index + 1}
                          </ThemedText>
                        </View>
                        <View style={styles.stepContent}>
                          <Body>{step.instruction || 'Continue'}</Body>
                          <View style={styles.stepMeta}>
                            <Caption color={theme.colors.textSecondary}>
                              {formatDistance(step.distance)} • {formatDuration(step.duration)}
                            </Caption>
                          </View>
                        </View>
                      </View>
                    ))}
                  </View>
                ) : (
                  <View style={styles.stepsContainer}>
                    <Body color={theme.colors.textSecondary} style={{ textAlign: 'center', paddingVertical: 24 }}>
                      No turn-by-turn instructions available for this route.
                    </Body>
                  </View>
                )}
              </>
            )}
          </ScrollView>

          {/* Footer Actions */}
          {route && !loading && (
            <View style={styles.footer}>
              <Button
                label="Open in Maps"
                onPress={handleOpenExternalMaps}
                variant="secondary"
                style={styles.footerButton}
                icon={<Ionicons name="open-outline" size={20} color={theme.colors.primary} />}
              />
            </View>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    // Note: backgroundColor is set inline with theme.colors.modalOverlay
    justifyContent: 'flex-end',
  },
  panel: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '90%',
    paddingBottom: Platform.OS === 'ios' ? 34 : 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  headerText: {
    flex: 1,
  },
  closeButton: {
    padding: 4,
  },
  modeSelector: {
    flexDirection: 'row',
    gap: 12,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  modeButton: {
    flex: 1,
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  errorContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  summaryCard: {
    marginBottom: 24,
  },
  summaryRow: {
    flexDirection: 'row',
    gap: 24,
  },
  summaryItem: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  summaryText: {
    flex: 1,
  },
  stepsContainer: {
    gap: 16,
  },
  sectionTitle: {
    marginBottom: 16,
  },
  stepItem: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#007AFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepContent: {
    flex: 1,
    gap: 4,
  },
  stepMeta: {
    marginTop: 4,
  },
  footer: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0, 0, 0, 0.1)',
  },
  footerButton: {
    width: '100%',
  },
});

